import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Users, Footprints, Percent, TrendingUp, Eye, CalendarDays, Layers, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { KpiTile, KpiSkeleton } from "@/components/exec/KpiTile";
import { SectionCard, PageHeader, ErrorState, ChartSkeleton, Stat, EmptyState } from "@/components/exec/Primitives";
import { TrendChart, WeekdayChart, HolidayLegend, GenderSplit, AgeBands } from "@/components/exec/Charts";
import { StoreTable, PatternSignals, DataNote, ScopeLink } from "@/components/exec/Widgets";
import { InsightCard, InsightsBody, InsightsHeaderActions, ExecutiveSummaryText, useInsights, useScrollOnActive } from "@/components/exec/Insights";
import { FeedAlertBanner } from "@/components/dashboard/FeedAlertBanner";
import { useCountV2 } from "@/lib/queries";
import { useScope } from "@/lib/scope";
import { useApp } from "@/lib/app";
import { errorMessage } from "@/lib/api";
import { fmtInt, fmtPct, fmtDate, fmtDateLong, fmtRange } from "@/lib/format";

const na = (k, txt) => (k && (k.value === null || k.value === undefined) ? txt : undefined);
const KIND_LABEL = { all: "All stores", cluster: "Mall", site: "Store" };

export default function CountAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();
  const { params, setScope } = useScope();
  const { isAdmin, feed } = useApp();
  const q = useCountV2();
  const d = q.data;
  const [metric, setMetric] = useState("entered");
  const ins = useInsights("count", params, !!d);
  const insightsRef = useScrollOnActive(location.hash === "#insights" && !!d);

  useEffect(() => {
    document.title = d ? `Zapin | ${d.scope.short} · Count` : "Zapin | Count Analytics";
  }, [d]);

  const awaiting = d && d.coverage.active_days === 0 && !d.coverage.open_day;
  const hasWir = !!d?.coverage?.feeds?.wir;
  const showWeekly = (d?.weekly?.length || 0) > 1;
  const stale = q.isFetching && q.isPlaceholderData;

  const ageBands = useMemo(() => (d?.age || []).filter((b) => b.count), [d]);

  return (
    <div className="space-y-6 md:space-y-8" data-testid="count-analytics-page">
      <PageHeader
        eyebrow={d ? `Count analytics · ${KIND_LABEL[d.scope.kind]}${d.scope.location ? ` · ${d.scope.location}` : ""}` : "Count analytics"}
        title={d ? d.scope.name : "Footfall, walk-in and who visits"}
        subtitle={d ? `${d.period.label} · ${d.period.compare_label}. Visitors are people who entered; walk-in rate is visitors over everyone who passed the door.` : "Loading…"}
        testId="count-header"
        actions={d && d.scope.kind !== "all" ? <ScopeLink to="/count" className="text-sm" onClick={() => setScope("all")}>Back to all stores</ScopeLink> : null}
      />

      <FeedAlertBanner feed={feed} siteIds={d?.scope?.site_ids || []} canOpenFeed={isAdmin} onNavigate={() => navigate("/admin/live")} />
      {q.isError && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} testId="count-error" />}

      {!d ? (
        <KpiSkeleton n={8} />
      ) : awaiting ? (
        <EmptyState icon={Eye} title="Awaiting data for this window" body={`${d.scope.name} has no closed trading days with counts between ${fmtDate(d.period.start)} and ${fmtDate(d.period.end)}. ${d.coverage.last_data_date ? `Latest counts on file: ${fmtDateLong(d.coverage.last_data_date)}.` : "The cameras have not posted for this scope yet."} Widen the period or pick another store.`} testId="count-awaiting" />
      ) : (
        <div className={stale ? "opacity-70 transition-opacity duration-200" : "transition-opacity duration-200"}>
          <DataNote coverage={d.coverage} period={d.period} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4" data-testid="count-kpi-strip">
            <KpiTile index={0} label="Visitors" kpi={d.kpis.visitors} icon={Users} compareLabel={d.period.compare_label} testId="kpi-visitors" />
            <KpiTile index={1} label="Avg visitors / day" kpi={d.kpis.avg_daily} icon={TrendingUp} testId="kpi-avg-daily" />
            <KpiTile index={2} label="Passing by" kpi={d.kpis.passing_by} icon={Footprints} sub={na(d.kpis.passing_by, "not captured")} hint="People who walked past the entrance without entering." testId="kpi-passing-by" />
            <KpiTile index={3} label="Walk-in rate" kpi={d.kpis.walk_in_rate} icon={Percent} sub={na(d.kpis.walk_in_rate, "needs passing-by")} hint="Visitors ÷ (visitors + passers-by). Computed from totals, not an average of daily rates." testId="kpi-walk-in" />
            <KpiTile index={4} label="Female share" kpi={d.kpis.female_share} icon={Users} sub={na(d.kpis.female_share, "gender not captured")} testId="kpi-female-share" />
            <KpiTile index={5} label="Families share" kpi={d.kpis.families_share} icon={Users} sub={na(d.kpis.families_share, "age not captured")} hint="Children + teenagers + adolescents as a share of visitors with an age estimate." testId="kpi-families" />
            <KpiTile index={6} label="Core 30–59" kpi={d.kpis.core_30_59_share} icon={Users} sub={na(d.kpis.core_30_59_share, "age not captured")} hint="Adults and middle-aged visitors - the core duty-free buying group." testId="kpi-core" />
            <KpiTile index={7} label="Weekend share" kpi={d.kpis.weekend_share} icon={CalendarDays} hint="Share of the period's visitors who came on Saturday or Sunday." testId="kpi-weekend-share" />
          </div>
        </div>
      )}

      {d && !awaiting && (
        <>
          <SectionCard
            title="Daily trend vs previous period"
            subtitle={`Solid line is ${d.period.label}; dashed is ${d.period.compare_label.replace(/^vs /, "")}. Markers follow the holiday overlay toggle.`}
            testId="count-trend-card"
            actions={
              <Tabs value={metric} onValueChange={setMetric}>
                <TabsList className="h-8 bg-muted/30" data-testid="count-metric-tabs">
                  <TabsTrigger value="entered" className="text-xs h-7 data-[state=active]:bg-gold/15" data-testid="count-metric-visitors">Visitors</TabsTrigger>
                  <TabsTrigger value="walk_in_rate" className="text-xs h-7 data-[state=active]:bg-gold/15" disabled={!hasWir} data-testid="count-metric-walkin">Walk-in rate</TabsTrigger>
                </TabsList>
              </Tabs>
            }
          >
            <TrendChart rows={d.daily} prevRows={d.prev_daily} events={d.calendar.events} metric={metric} height={340} testId="count-analytics-walkin-rate-chart" />
            <HolidayLegend className="mt-3" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              <Stat label="Peak day" value={d.kpis.peak?.value ? fmtInt(d.kpis.peak.value) : "—"} sub={d.kpis.peak?.date ? fmtDateLong(d.kpis.peak.date) : ""} testId="count-peak" />
              <Stat label="Quietest day" value={d.kpis.low?.value ? fmtInt(d.kpis.low.value) : "—"} sub={d.kpis.low?.date ? fmtDateLong(d.kpis.low.date) : ""} testId="count-low" />
              <Stat label="Weekend avg / day" value={d.summary.weekend_avg ? fmtInt(d.summary.weekend_avg) : "—"} sub={d.summary.weekday_avg ? `weekday ${fmtInt(d.summary.weekday_avg)}` : "no weekend days yet"} testId="count-weekend-avg" />
              <Stat label="Closed days" value={`${d.coverage.active_days} / ${d.coverage.days_in_period}`} sub={d.coverage.prev_active_days ? `${d.coverage.prev_active_days} in comparison` : "no comparison data"} testId="count-closed-days" />
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <SectionCard className="lg:col-span-7" title="Weekday profile" subtitle="Average visitors per weekday in this period (bars) against the comparison window (dashed)." testId="count-weekday-card">
              <WeekdayChart rows={d.by_weekday} height={260} testId="count-weekday-chart" />
              <div className="mt-3 flex flex-wrap gap-1.5">
                {d.by_weekday.filter((w) => w.index).map((w) => (
                  <span key={w.day} className="text-[11px] font-mono rounded-md border border-border/70 bg-muted/20 px-2 py-0.5 text-muted-foreground" data-testid={`weekday-index-${w.day}`}>{w.day} {w.index}</span>
                ))}
                <span className="text-[11px] text-muted-foreground self-center ml-1">index · 100 = period average</span>
              </div>
            </SectionCard>
            <SectionCard className="lg:col-span-5" title="Who visits" subtitle="Gender and age estimates from the cameras. Deltas are percentage points vs the comparison window." testId="count-demographics-card">
              <div className="space-y-5">
                <GenderSplit gender={d.gender} testId="count-gender" />
                <div>
                  <div className="eyebrow mb-2">Age bands</div>
                  <AgeBands bands={ageBands} testId="count-age-bands" />
                </div>
              </div>
            </SectionCard>
          </div>

          {d.stores.length > 1 && (
            <SectionCard title="Store comparison" subtitle="Every store in scope, ranked by visitors. Click a row to focus on that store." testId="count-stores-card" actions={<Layers size={15} className="text-gold" />}>
              <StoreTable stores={d.stores} compareLabel={d.period.compare_label} testId="count-store-table" />
            </SectionCard>
          )}

          {showWeekly && (
            <SectionCard title="Week by week" subtitle="ISO weeks inside the period (closed days only)." testId="count-weekly-card">
              <div className="overflow-x-auto scroll-thin -mx-1">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-border/60">
                      <TableHead className="text-[11px] uppercase tracking-[0.14em]">Week</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Visitors</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Avg / day</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Days</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Walk-in</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.weekly.map((w) => (
                      <TableRow key={w.week_id} className="border-border/50 hover:bg-muted/20" data-testid={`week-row-${w.week_id}`}>
                        <TableCell className="text-sm">{fmtRange(w.start, w.end)}</TableCell>
                        <TableCell className="text-right font-mono tabular text-sm">{fmtInt(w.visitors)}</TableCell>
                        <TableCell className="text-right font-mono tabular text-xs">{fmtInt(w.avg_daily)}</TableCell>
                        <TableCell className="text-right font-mono tabular text-xs text-muted-foreground">{w.days}</TableCell>
                        <TableCell className="text-right font-mono tabular text-xs">{w.walk_in_rate !== null && w.walk_in_rate !== undefined ? fmtPct(w.walk_in_rate) : <span className="text-muted-foreground">n/c</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}

          <SectionCard title="Pattern signals" subtitle="Deterministic checks on this exact window: trend, weekday shape, holiday and payday lift, anomalies, mix of visitors, cross-store flows." testId="count-patterns-card">
            <PatternSignals patterns={d.patterns} limit={6} testId="count-pattern-signals" />
          </SectionCard>

          <section ref={insightsRef} id="insights" className="card-surface p-4 md:p-5 scroll-mt-24" data-testid="count-insights-card">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h3 className="font-display text-base md:text-lg leading-tight inline-flex items-center gap-2"><Sparkles size={16} className="text-gold" /> AI insights for this view</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Consultant-grade reading of the evidence above. Standard week and month views are prepared nightly; any scope or period can be generated on demand.</p>
              </div>
              <InsightsHeaderActions state={{ ...ins.data, starting: ins.starting, isLoading: ins.isLoading }} onGenerate={ins.generate} />
            </div>
            <InsightsBody
              state={ins.isLoading ? { isLoading: true } : ins.data}
              kind="count"
              renderReady={(data) => (
                <div className="space-y-4">
                  <ExecutiveSummaryText text={data.executive_summary} watchlist={data.watchlist} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="count-insight-grid">
                    {(data.insights || []).map((i, idx) => <InsightCard key={i.id || idx} insight={i} index={idx} />)}
                  </div>
                </div>
              )}
            />
          </section>
        </>
      )}

      {!d && !q.isError && (
        <div className="space-y-4">
          <ChartSkeleton height={340} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4"><Skeleton className="lg:col-span-7 h-72 rounded-xl" /><Skeleton className="lg:col-span-5 h-72 rounded-xl" /></div>
        </div>
      )}
    </div>
  );
}
